import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CheckCircle, Plus } from "lucide-react";
import type { CustomerCredit, CreditType, CreditStatus, CreditReasonCategory, CreditAttributionType, Issue } from "@/data/types";

const CREDIT_TYPES: CreditType[] = [
  "Goodwill Credit", "Service Recovery Credit", "Material / Damage Credit",
  "Install Issue Credit", "Pricing Adjustment", "Other",
];
const REASON_CATEGORIES: CreditReasonCategory[] = [
  "Quality Issue", "Scheduling Delay", "Material Defect", "Customer Complaint",
  "Install Error", "Pricing Error", "Service Failure", "Other",
];
const ATTRIBUTION_TYPES: CreditAttributionType[] = [
  "Crew-Related", "Material-Related", "Customer Goodwill", "Vendor-Related", "Unknown",
];
const CREDIT_STATUSES: CreditStatus[] = [
  "Draft", "Proposed", "Approved", "Offered to Customer", "Accepted", "Applied", "Declined", "Canceled",
];

interface CreateCreditFormProps {
  jobId: string;
  customerId: string;
  issueId?: string;
  crewName?: string;
  subcontractorId?: string;
  availableIssues?: Issue[];
  onSubmit: (credit: CustomerCredit) => void;
  onCancel: () => void;
}

export function CreateCreditForm({
  jobId, customerId, issueId, crewName, subcontractorId,
  availableIssues, onSubmit, onCancel,
}: CreateCreditFormProps) {
  const [amount, setAmount] = useState("");
  const [creditType, setCreditType] = useState<CreditType>("Goodwill Credit");
  const [reasonCategory, setReasonCategory] = useState<CreditReasonCategory>("Other");
  const [attributionType, setAttributionType] = useState<CreditAttributionType>("Unknown");
  const [status, setStatus] = useState<CreditStatus>("Draft");
  const [notes, setNotes] = useState("");
  const [selectedIssueId, setSelectedIssueId] = useState(issueId || "");
  const [crew, setCrew] = useState(crewName || "");
  const [contractor, setContractor] = useState(subcontractorId || "");
  const [createdBy, setCreatedBy] = useState("Ops Manager");

  const handleSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) return;
    const credit: CustomerCredit = {
      id: `CR-${Date.now().toString(36).toUpperCase()}`,
      customerId,
      jobId,
      issueId: selectedIssueId || undefined,
      relatedContractorId: contractor || undefined,
      relatedCrewName: crew || undefined,
      creditAmount: parseFloat(amount),
      creditType,
      reasonCategory,
      attributionType,
      notes,
      status,
      dateCreated: new Date().toISOString().split("T")[0],
      createdBy,
    };
    onSubmit(credit);
  };

  return (
    <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5">
        <Plus className="h-3.5 w-3.5" /> New Credit / Resolution
      </h4>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[11px]">Credit Amount *</Label>
          <Input
            type="number" min="0" step="0.01" placeholder="0.00"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Credit Type</Label>
          <Select value={creditType} onValueChange={(v) => setCreditType(v as CreditType)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{CREDIT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Reason Category</Label>
          <Select value={reasonCategory} onValueChange={(v) => setReasonCategory(v as CreditReasonCategory)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{REASON_CATEGORIES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Attribution Type</Label>
          <Select value={attributionType} onValueChange={(v) => setAttributionType(v as CreditAttributionType)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{ATTRIBUTION_TYPES.map((a) => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as CreditStatus)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{CREDIT_STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Created By</Label>
          <Input value={createdBy} onChange={(e) => setCreatedBy(e.target.value)} className="h-8 text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[11px]">Assigned Crew</Label>
          <Input value={crew} onChange={(e) => setCrew(e.target.value)} placeholder="Crew name" className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Subcontractor ID</Label>
          <Input value={contractor} onChange={(e) => setContractor(e.target.value)} placeholder="Contractor ID" className="h-8 text-sm" />
        </div>
      </div>

      {availableIssues && availableIssues.length > 0 && !issueId && (
        <div className="space-y-1">
          <Label className="text-[11px]">Related Issue (optional)</Label>
          <Select value={selectedIssueId} onValueChange={setSelectedIssueId}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select an issue..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" className="text-xs">No linked issue</SelectItem>
              {availableIssues.map((i) => (
                <SelectItem key={i.id} value={i.id} className="text-xs">{i.id} — {i.issueType} ({i.severity})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-[11px]">Notes</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Credit details and context..." className="text-sm min-h-[50px]" rows={2} />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" className="gap-1 text-xs" onClick={handleSubmit} disabled={!amount || parseFloat(amount) <= 0}>
          <CheckCircle className="h-3 w-3" /> Create Credit
        </Button>
        <Button size="sm" variant="outline" className="text-xs" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
